import {
  DOMAINS,
  type AttemptResult,
  type Difficulty,
  type DomainMastery
} from "@/domain";

import {
  calculateAttemptScore,
  clampDifficulty,
  clampScore,
  type AttemptScoringOptions
} from "./scoring";

export const RECENT_ATTEMPT_WEIGHT = 0.35;
export const DIFFICULTY_INCREASE_SCORE = 80;
export const DIFFICULTY_DECREASE_SCORE = 45;

function compareText(left: string, right: string): number {
  if (left < right) {
    return -1;
  }

  return left > right ? 1 : 0;
}

function compareAttempts(left: AttemptResult, right: AttemptResult): number {
  return (
    compareText(left.completedAt, right.completedAt) ||
    compareText(left.id, right.id)
  );
}

export function recommendNextDifficulty(
  currentDifficulty: Difficulty,
  attemptScore: number
): Difficulty {
  const difficulty = clampDifficulty(currentDifficulty);
  const score = clampScore(attemptScore);

  if (score >= DIFFICULTY_INCREASE_SCORE) {
    return clampDifficulty(difficulty + 1);
  }

  if (score < DIFFICULTY_DECREASE_SCORE) {
    return clampDifficulty(difficulty - 1);
  }

  return difficulty;
}

export function updateDomainMastery(
  previous: DomainMastery | undefined,
  attempt: AttemptResult,
  scoringOptions: AttemptScoringOptions = {}
): DomainMastery {
  if (previous !== undefined && previous.domain !== attempt.domain) {
    throw new Error(
      "Cannot update " +
        previous.domain +
        " mastery with a " +
        attempt.domain +
        " attempt."
    );
  }

  const attemptScore = calculateAttemptScore(attempt, scoringOptions);
  const score =
    previous === undefined
      ? attemptScore
      : Math.round(
          clampScore(previous.score) * (1 - RECENT_ATTEMPT_WEIGHT) +
            attemptScore * RECENT_ATTEMPT_WEIGHT
        );
  const currentDifficulty =
    previous?.recommendedDifficulty ?? attempt.difficulty;

  return {
    domain: attempt.domain,
    score: clampScore(score),
    recommendedDifficulty: recommendNextDifficulty(
      currentDifficulty,
      attemptScore
    ),
    updatedAt: attempt.completedAt
  };
}

export function calculateMasteryForProfile(
  profileId: string,
  attempts: readonly AttemptResult[],
  scoringOptions: AttemptScoringOptions = {}
): DomainMastery[] {
  const orderedAttempts = attempts
    .filter((attempt) => attempt.profileId === profileId)
    .sort(compareAttempts);
  const masteryByDomain = new Map<DomainMastery["domain"], DomainMastery>();

  for (const attempt of orderedAttempts) {
    masteryByDomain.set(
      attempt.domain,
      updateDomainMastery(
        masteryByDomain.get(attempt.domain),
        attempt,
        scoringOptions
      )
    );
  }

  const result: DomainMastery[] = [];

  for (const domain of DOMAINS) {
    const mastery = masteryByDomain.get(domain);

    if (mastery !== undefined) {
      result.push(mastery);
    }
  }

  return result;
}
